/**
 * MobileNav — barra inferior fija (solo teléfono).
 *
 * Por qué existe: en móvil, llegar a cualquier sección costaba tres toques
 * (hamburguesa → recorrer el menú → elegir). Aquí lo que cada rol usa a
 * diario está a UN toque, y el resto sigue en el menú detrás de «Más».
 *
 * Para el paciente, el botón central es la acción principal del producto
 * —registrar una comida—, elevado en cempasúchil: desde cualquier pantalla,
 * un toque. (Cempasúchil = celebración/CTA; jamás rojo, regla R3.)
 */
import React from 'react';
import { Box, Typography, Badge } from '@mui/material';
import {
  Home as HomeIcon,
  CameraAlt as CameraIcon,
  Spa as SpaIcon,
  Chat as ChatIcon,
  MoreHoriz as MoreIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  Fastfood as FastfoodIcon,
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';

// 4 destinos + «Más». Más de cinco ranuras y los blancos se vuelven
// imposibles de acertar con el pulgar en una pantalla de 390px.
const TABS = {
  guest: [
    { id: 'dashboard', label: 'Inicio', icon: HomeIcon },
    { id: 'medicinal-plants', label: 'Plantas', icon: SpaIcon },
    { id: 'foods', label: 'Alimentos', icon: RestaurantIcon },
    { id: 'calculator', label: 'Calcular', icon: CalculateIcon },
  ],
  patient: [
    { id: 'dashboard', label: 'Inicio', icon: HomeIcon },
    { id: 'medicinal-plants', label: 'Plantas', icon: SpaIcon },
    { id: 'food-log', label: 'Registrar', icon: CameraIcon, primary: true },
    { id: 'chat-ia', label: 'Huitzi', icon: ChatIcon },
  ],
  nutritionist: [
    { id: 'dashboard', label: 'Inicio', icon: HomeIcon },
    { id: 'patients', label: 'Pacientes', icon: PeopleIcon },
    { id: 'appointments', label: 'Agenda', icon: EventNoteIcon },
    { id: 'dietas', label: 'Dietas', icon: FastfoodIcon },
  ],
  admin: [
    { id: 'dashboard', label: 'Inicio', icon: HomeIcon },
    { id: 'patients', label: 'Pacientes', icon: PeopleIcon },
    { id: 'foods', label: 'Alimentos', icon: RestaurantIcon },
    { id: 'recipes', label: 'Recetas', icon: MenuBookIcon },
  ],
};

export const MOBILE_NAV_HEIGHT = 62;

const MobileNav = ({ currentView, setCurrentView, currentRole, onOpenMenu, notificationsCount = 0 }) => {
  const tabs = TABS[currentRole] || TABS.guest;

  const go = (id) => {
    setCurrentView(id);
    // El pulgar ya cambió de pantalla: llévala arriba, no a media página.
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <Box
      component="nav"
      aria-label="Navegación principal"
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        alignItems: 'stretch',
        bgcolor: '#FBF5E9',
        borderTop: '1px solid',
        borderColor: 'divider',
        // La barra se apoya en el borde inferior del iPhone: sin esto, el
        // indicador de inicio se come la fila de etiquetas.
        pb: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -2px 14px rgba(40,30,12,0.10)',
      }}
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = currentView === t.id;

        if (t.primary) {
          return (
            <Box
              key={t.id}
              component="button"
              type="button"
              onClick={() => go(t.id)}
              aria-label={t.label}
              aria-current={active ? 'page' : undefined}
              sx={{
                flex: 1, border: 0, background: 'transparent', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-end', gap: 0.25, pb: 0.75, pt: 0,
                minHeight: MOBILE_NAV_HEIGHT, font: 'inherit', color: 'inherit',
                '&:focus-visible': { outline: '3px solid #2C7D8C', outlineOffset: -3 },
              }}
            >
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: '50%',
                  bgcolor: '#E0892E', color: '#16201A',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mt: -2.5,
                  boxShadow: '0 4px 14px rgba(224,137,46,0.45)',
                  border: '3px solid #FBF5E9',
                  transition: 'transform .18s cubic-bezier(.2,.8,.2,1)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  ...(active && { transform: 'scale(1.06)' }),
                }}
              >
                <Icon sx={{ fontSize: 26 }} />
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1F5C3D', lineHeight: 1 }}>
                {t.label}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            key={t.id}
            component="button"
            type="button"
            onClick={() => go(t.id)}
            aria-label={t.label}
            aria-current={active ? 'page' : undefined}
            sx={{
              flex: 1, border: 0, background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 0.4,
              minHeight: MOBILE_NAV_HEIGHT, font: 'inherit',
              color: active ? '#1F5C3D' : '#7A7E6C',
              position: 'relative',
              '&:focus-visible': { outline: '3px solid #2C7D8C', outlineOffset: -3 },
              // Marca de pestaña activa: una barra corta arriba, no solo color
              // (el color por sí solo no es señal suficiente — a11y).
              '&::before': active ? {
                content: '""', position: 'absolute', top: 0, left: '50%',
                transform: 'translateX(-50%)', width: 26, height: 3,
                borderRadius: '0 0 3px 3px', background: '#1F5C3D',
              } : {},
            }}
          >
            <Icon sx={{ fontSize: 23 }} />
            <Typography sx={{ fontSize: 11, fontWeight: active ? 700 : 500, lineHeight: 1 }}>
              {t.label}
            </Typography>
          </Box>
        );
      })}

      <Box
        component="button"
        type="button"
        onClick={onOpenMenu}
        aria-label="Abrir menú completo"
        sx={{
          flex: 1, border: 0, background: 'transparent', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 0.4,
          minHeight: MOBILE_NAV_HEIGHT, font: 'inherit', color: '#7A7E6C',
          '&:focus-visible': { outline: '3px solid #2C7D8C', outlineOffset: -3 },
        }}
      >
        <Badge color="secondary" variant="dot" invisible={!notificationsCount}>
          <MoreIcon sx={{ fontSize: 23 }} />
        </Badge>
        <Typography sx={{ fontSize: 11, fontWeight: 500, lineHeight: 1 }}>Más</Typography>
      </Box>
    </Box>
  );
};

export default MobileNav;
